import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../../lib/api-client";
import { createWish, previewWish } from "../../lib/api-wish";
import type { WishPreview, WishPriority, WishResponse } from "../../types/wish";
import { OGPreview, type PreviewStatus } from "./OGPreview";
import { WishEventPicker } from "./WishEventPicker";

interface AddWishFormProps {
  /** UUID of the family_member whose wishlist this wish belongs to. */
  memberId: string;
  /**
   * Called with the created wish on success. When omitted the form navigates
   * to the member's wishlist instead.
   */
  onSuccess?: (wish: WishResponse) => void;
}

const URL_DEBOUNCE_MS = 600;
const MAX_TITLE_LEN = 500;
const MAX_DESCRIPTION_LEN = 2000;

const GENERIC_ERROR = "Не удалось добавить желание. Попробуйте ещё раз.";
const FORBIDDEN_ERROR = "Нельзя добавить желание от имени другого участника";
const NOT_FOUND_ERROR = "Список желаний не найден";

const PRIORITY_OPTIONS: ReadonlyArray<{
  value: WishPriority;
  label: string;
}> = [
  { value: "high", label: "Высокий" },
  { value: "medium", label: "Средний" },
  { value: "low", label: "Низкий" },
];

/** Returns true when the string is a valid absolute http(s) URL. */
function isValidUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) {
    return false;
  }
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export function AddWishForm({ memberId, onSuccess }: AddWishFormProps) {
  const navigate = useNavigate();
  const titleId = useId();
  const urlId = useId();
  const descriptionId = useId();
  const errorId = useId();
  const titleErrorId = useId();
  const priorityGroupId = useId();
  const titleRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<WishPriority>("medium");
  const [eventID, setEventID] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [previewState, setPreviewState] = useState<{
    url: string;
    status: PreviewStatus;
    data: WishPreview | null;
  } | null>(null);
  const previewAbortRef = useRef<AbortController | null>(null);
  const previewRequestRef = useRef(0);
  const autoTitleRef = useRef("");

  async function loadPreview(rawUrl: string) {
    previewAbortRef.current?.abort();
    const controller = new AbortController();
    const requestID = ++previewRequestRef.current;
    previewAbortRef.current = controller;
    setPreviewState({ url: rawUrl, status: "loading", data: null });
    try {
      const data = await previewWish(rawUrl, controller.signal);
      if (controller.signal.aborted || requestID !== previewRequestRef.current) return;
      setPreviewState({ url: rawUrl, status: "success", data });
      const discoveredTitle = data.title;
      if (discoveredTitle) {
        setTitleError(null);
        setTitle((current) => {
          if (current.trim() !== "" && current !== autoTitleRef.current) return current;
          autoTitleRef.current = discoveredTitle;
          return discoveredTitle;
        });
      }
    } catch {
      if (controller.signal.aborted || requestID !== previewRequestRef.current) return;
      setPreviewState({ url: rawUrl, status: "error", data: null });
    }
  }

  useEffect(() => {
    previewAbortRef.current?.abort();
    previewRequestRef.current++;
    setPreviewState(null);
    const trimmed = url.trim();
    if (trimmed === "" || !isValidUrl(trimmed)) return;
    const timeout = setTimeout(() => void loadPreview(trimmed), URL_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
      previewAbortRef.current?.abort();
    };
  }, [url]);

  const trimmedTitle = title.trim();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;
    if (trimmedTitle === "") {
      setTitleError("Введите название желания.");
      titleRef.current?.focus();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const trimmedUrl = url.trim();
    const trimmedDescription = description.trim();
    const confirmedPreview =
      previewState?.status === "success" && previewState.url === trimmedUrl
        ? previewState.data
        : null;

    try {
      const wish = await createWish(memberId, {
        title: trimmedTitle,
        url: trimmedUrl || undefined,
        description: trimmedDescription || undefined,
        priority,
        ...(confirmedPreview?.image_url ? { image_url: confirmedPreview.image_url } : {}),
        ...(confirmedPreview?.price != null ? { price: confirmedPreview.price } : {}),
        ...(eventID ? { event_id: eventID } : {}),
      });
      if (onSuccess) {
        onSuccess(wish);
      } else {
        navigate(`/members/${memberId}/wishes`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "FORBIDDEN") {
        setError(FORBIDDEN_ERROR);
      } else if (err instanceof ApiError && err.code === "NOT_FOUND") {
        setError(NOT_FOUND_ERROR);
      } else {
        setError(GENERIC_ERROR);
      }
      setIsSubmitting(false);
    }
  }

  const inputClasses =
    "form-control h-13 w-full rounded-2xl border border-line bg-white px-4 focus:outline-none disabled:cursor-not-allowed disabled:text-muted";
  const labelClasses = "mb-2 block text-[13px] font-bold text-content";

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col bg-canvas md:min-h-full lg:py-8">
        <header className="relative flex items-center justify-center px-4 py-5">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Назад"
          className="absolute left-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white text-content shadow-sm transition-transform hover:-translate-x-0.5"
        >
          <span aria-hidden="true" className="text-xl leading-none">
            &lt;
          </span>
        </button>
        <h1 className="text-lg font-extrabold tracking-[-0.03em] text-content">Добавить желание</h1>
      </header>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="mx-4 flex flex-1 flex-col gap-5 rounded-t-[28px] bg-white px-5 py-6 shadow-[0_-8px_30px_rgba(35,33,25,0.04)] lg:grid lg:flex-none lg:grid-cols-2 lg:gap-6 lg:rounded-[28px] lg:px-8 lg:shadow-[0_16px_42px_rgba(35,33,25,0.07)]"
      >
        <div>
          <label htmlFor={titleId} className={labelClasses}>
            Название *
          </label>
          <input
            ref={titleRef}
            id={titleId}
            type="text"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              if (titleError) setTitleError(null);
              if (error) setError(null);
            }}
            maxLength={MAX_TITLE_LEN}
            placeholder="Например, Набор акварели Schmincke"
            autoComplete="off"
            required
            disabled={isSubmitting}
            aria-invalid={titleError !== null}
            aria-describedby={titleError ? titleErrorId : undefined}
            className={inputClasses}
          />

          {titleError && (
            <div id={titleErrorId} role="alert" className="mt-2 text-[13px] font-medium text-danger">
              {titleError}
            </div>
          )}

          {error && (
            <div
              id={errorId}
              role="alert"
              className="mt-2 rounded-lg border-l-4 border-danger bg-danger-soft px-3 py-2.5 text-[13px] font-medium text-danger"
            >
              {error}
            </div>
          )}
        </div>

        <div>
          <label htmlFor={urlId} className={labelClasses}>
            Ссылка (опционально)
          </label>
          <input
            id={urlId}
            type="url"
            inputMode="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            maxLength={2048}
            placeholder="https://..."
            autoComplete="off"
            disabled={isSubmitting}
            aria-describedby={`${urlId}-hint`}
            className={inputClasses}
          />
          <p id={`${urlId}-hint`} className="mt-2 text-xs leading-5 text-muted">
            Можно указать только название — ссылка не обязательна.
          </p>
          <span className="sr-only" role="status" aria-live="polite">
            {previewState?.status === "loading" && "Ищем название и фото…"}
            {previewState?.status === "success" && "Данные ссылки загружены."}
            {previewState?.status === "error" && "Не удалось загрузить данные. Заполните желание вручную."}
          </span>
          <OGPreview
            url={previewState?.url ?? ""}
            status={previewState?.status}
            preview={previewState?.data}
            onRetry={previewState?.status === "error" ? () => void loadPreview(previewState.url) : undefined}
          />
        </div>

        <div>
          <label htmlFor={descriptionId} className={labelClasses}>
            Описание (опционально)
          </label>
          <textarea
            id={descriptionId}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={MAX_DESCRIPTION_LEN}
            rows={3}
            placeholder="Любые дополнительные пожелания…"
            disabled={isSubmitting}
            className="form-control w-full resize-none rounded-2xl border border-line bg-white px-4 py-3 focus:outline-none disabled:cursor-not-allowed"
          />
        </div>

        <fieldset disabled={isSubmitting} className="border-0 p-0">
          <legend id={priorityGroupId} className={labelClasses}>
            Приоритет
          </legend>
          <div
            role="radiogroup"
            aria-labelledby={priorityGroupId}
            className="flex gap-1 rounded-2xl bg-surface-subtle p-1"
          >
            {PRIORITY_OPTIONS.map((option, index) => {
              const selected = priority === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setPriority(option.value)}
                  className={`flex-1 px-3 py-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action disabled:cursor-not-allowed ${
                    index > 0 ? "" : ""
                  } ${
                    selected
                      ? "rounded-xl bg-content text-white"
                      : "rounded-xl text-muted hover:bg-white"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <WishEventPicker
          value={eventID}
          onChange={setEventID}
          disabled={isSubmitting}
        />

        <div className="mt-auto pt-4 lg:col-span-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`h-[52px] w-full rounded-xl text-base font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              isSubmitting
                ? "cursor-not-allowed bg-surface-disabled"
                : "cursor-pointer bg-action shadow-[0_10px_24px_rgba(194,65,12,0.24)] hover:bg-action-hover"
            }`}
          >
            {isSubmitting ? "Сохраняем…" : "Добавить желание"}
          </button>
        </div>
      </form>
    </div>
  );
}
